/* eslint-disable react/require-default-props */
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ContentSection, Fieldset, Form, Heading, Layout, Paragraph } from '@vwfs-bronson/bronson-react';
import axios from 'axios';
import { Formik, FormikProps } from 'formik';
import { FileUpload, parseAndDownloadBinaryFileBlob, SUPPORTED_FILE_FORMATS } from '../../../components/FileUpload';
import { FormNavigationButtons } from '../../../components/FormNavigationButtons';
import { SpinnerSection } from '../../../components/SpinnerSection';
import { Analytics } from '../../../services/analytics';
import { documentUploadEvent, interActionEvent, legitimationEvent } from '../../../services/analytics/analyticsEvents';
import { largeFileDownload, largeFileUpload } from '../../../services/api/largeFileHandling/largeFileHandling';
import {
  deleteCreditApplicationCoCustomerStipulationDocument,
  deleteCreditApplicationStipulationDocument,
  getCreditApplication,
  getCreditApplicationCoCustomerStipulations,
  getCreditApplicationCoCustomerStipulationsDocuments,
  getCreditApplicationCoCustomerStipulationsSingleDocument,
  getCreditApplicationCustomerStipulations,
  getCreditApplicationCustomerStipulationsDocuments,
  getCreditApplicationCustomerStipulationsSingleDocument,
  uploadCreditApplicationCoCustomerStipulationDocument,
  uploadCreditApplicationStipulationDocument,
} from '../../../services/api/miles/miles-credit-services';
import { CreditApplicationStipulation } from '../../../services/api/miles/types';
import { assertDefined } from '../../../services/common/miscUtils';
import { store } from '../../../services/redux';
import {
  creditApplicationSelectors,
  setCreditApplicationDetails,
} from '../../../services/redux/features/creditApplication.slice';
import {
  selectCreditApplicationIdFromDataStorage,
  selectQuoteIdFromDataStorage,
} from '../../../services/redux/features/dataStorage.redux';
import { loadingActions } from '../../../services/redux/features/loading.slice';
import { ConfirmationPage } from './confirmationPage';
import { validationSchemaForStep5 } from './validationSchema';

interface FormStepProps {
  savedDocuments?: boolean;
  setSavedDocuments?: any;
  timeOutError?: boolean;
}

export type DocumentsUploadValues = {
  files: File[];
};

export const nonMandatoryStipulationType = 'OTHER_STIPULATION_CREDIT_APPLICATION';

const mandatorySipulations = (stipulation: CreditApplicationStipulation) =>
  stipulation.stipulationType !== nonMandatoryStipulationType;

const optionalSipulations = (stipulation: CreditApplicationStipulation) =>
  stipulation.stipulationType === nonMandatoryStipulationType;

const FormStep: FunctionComponent<FormikProps<any> & FormStepProps & React.PropsWithChildren> = (props) => {
  const {
    setFieldValue,
    setFieldError,
    errors,
    isValidating,
    isSubmitting,
    values,
    savedDocuments,
    setSavedDocuments,
    children,
  } = props;

  const dispatch = useDispatch();

  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [customerStipulations, setCustomerStipulations] = useState<CreditApplicationStipulation[]>([]);
  const [coCustomerStipulations, setCoCustomerStipulations] = useState<CreditApplicationStipulation[]>([]);
  const [mandatoryCustomerStipulations, setMandatoryCustomerStipulations] = useState<CreditApplicationStipulation[]>(
    []
  );
  const [optionalCustomerStipulations, setOptionalCustomerStipulations] = useState<CreditApplicationStipulation[]>([]);
  const [mandatoryCoCustomerStipulations, setMandatoryCoCustomerStipulations] = useState<
    CreditApplicationStipulation[]
  >([]);
  const [optionalCoCustomerStipulations, setOptionalCoCustomerStipulations] = useState<CreditApplicationStipulation[]>(
    []
  );

  const quoteId = useSelector(selectQuoteIdFromDataStorage)!;
  const storageCreditApplicationId = useSelector(selectCreditApplicationIdFromDataStorage)!;
  const { creditApplicationId, applicantCreditScoreIdCoCustomer } = useSelector(
    creditApplicationSelectors.selectCreditApplicationIds
  )!;

  useEffect(() => {
    const restoreMissingCreditApplicationDetails = async () => {
      if (storageCreditApplicationId && storageCreditApplicationId !== creditApplicationId) {
        const creditApplication = await getCreditApplication(storageCreditApplicationId);
        dispatch(setCreditApplicationDetails(creditApplication));
      }
    };

    restoreMissingCreditApplicationDetails();
  }, [storageCreditApplicationId, creditApplicationId, dispatch]);

  useEffect(() => {
    setMandatoryCustomerStipulations(customerStipulations.filter(mandatorySipulations));
    setOptionalCustomerStipulations(customerStipulations.filter(optionalSipulations));
  }, [customerStipulations]);

  useEffect(() => {
    setMandatoryCoCustomerStipulations(coCustomerStipulations.filter(mandatorySipulations));
    setOptionalCoCustomerStipulations(coCustomerStipulations.filter(optionalSipulations));
  }, [coCustomerStipulations]);

  const { t } = useTranslation();
  const [pendingStipulations, setPendingStipulations] = useState<string[]>([]);
  useEffect(() => {
    document.title = t('step5:title');
  }, [t]);

  const createStipulationFieldId = (stipulation) =>
    `credit-application-stipulation-${stipulation.stipulationId}-${stipulation.stipulationType}`;

  useEffect(() => {
    const errorElem = document.querySelector('.is-error') || document.querySelector('.c-error-message');
    if (
      errors !== undefined &&
      Object.entries(errors).length !== 0 &&
      errors.constructor === Object &&
      !isValidating &&
      !isSubmitting &&
      errorElem
    ) {
      errorElem.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
      const errorMessage = Object.values(errors)[0];
      Analytics.push(interActionEvent(errorMessage));
    }
  }, [isValidating, isSubmitting, errors]);

  const handleFieldData = async (e, type, stipulationId, coApplicantId?: string) => {
    const file = e.target.files[0];
    const alreadyUploadedFileId = values[type][0]?.id;

    try {
      if (alreadyUploadedFileId?.length > 0) {
        await deleteCreditApplicationStipulationDocument(
          quoteId,
          assertDefined(creditApplicationId),
          stipulationId,
          alreadyUploadedFileId
        );
      }

      await validationSchemaForStep5.validate({
        [type]: {
          mimeType: file.type,
          size: file.size,
        },
      });
      setFieldError(type, undefined);
    } catch (error: any) {
      const { errors } = error;
      setFieldError(type, errors[0]);
      setPendingStipulations((prevStipulations) => prevStipulations.filter((id) => id !== stipulationId));
      return;
    }

    setPendingStipulations([...pendingStipulations, stipulationId]);

    const fileRequest: MediaStorageService.Schemas.FilesRequestPost = {
      file: {
        name: file.name,
        mimeType: file.type,
        expirationDays: 1,
        createS3Uri: true,
        fileSize: file.size,
        url: {
          ttl: 30,
        },
      },
    };

    try {
      const mediaStorageUploadRes = await largeFileUpload(fileRequest);

      const { url } = mediaStorageUploadRes.file;

      if (url?.temporalUrl) {
        await axios.put(url?.temporalUrl, file, {
          headers: {
            'Content-Type': file.type,
          },
        });
        let res;
        if (coApplicantId) {
          res = await uploadCreditApplicationCoCustomerStipulationDocument(
            quoteId,
            assertDefined(creditApplicationId),
            coApplicantId,
            stipulationId,
            {
              id: mediaStorageUploadRes.file.id,
              mimeType: mediaStorageUploadRes.file.mimeType,
            }
          );
        } else {
          res = await uploadCreditApplicationStipulationDocument(
            quoteId,
            assertDefined(creditApplicationId),
            stipulationId,
            {
              id: mediaStorageUploadRes.file.id,
              mimeType: mediaStorageUploadRes.file.mimeType,
            }
          );
        }

        let filename = file.name.replace(/.[a-z]{3,4}$/, '');
        filename = `${filename.replace(/\./g, '-')}.pdf`;

        const fileRecord = {
          name: filename,
          mimeType: 'application/pdf',
          tags: [type, `${res.documents[0].documentId}`],
          id: res.documents[0].documentId,
          file,
        };
        setFieldValue(type, [fileRecord]);
        setPendingStipulations((prevStipulations) => prevStipulations.filter((id) => id !== stipulationId));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setPendingStipulations((prevStipulations) => prevStipulations.filter((id) => id !== stipulationId));
    }
  };

  const deleteStipulationDocument = async (
    stipulation: CreditApplicationStipulation,
    documentId: string,
    coApplicantId?: string
  ) => {
    const { stipulationId } = stipulation;
    setPendingStipulations([...pendingStipulations, stipulationId]);
    try {
      if (coApplicantId) {
        await deleteCreditApplicationCoCustomerStipulationDocument(
          quoteId,
          assertDefined(creditApplicationId),
          coApplicantId,
          stipulationId,
          documentId
        );
      } else {
        await deleteCreditApplicationStipulationDocument(
          quoteId,
          assertDefined(creditApplicationId),
          stipulationId,
          documentId
        );
      }
      const fieldId = createStipulationFieldId(stipulation);
      const newFieldValues = values[fieldId]?.filter((record) => record.id !== documentId) || [];
      setFieldValue(fieldId, newFieldValues);
      setPendingStipulations((prevStipulations) => prevStipulations.filter((id) => id !== stipulationId));
    } catch (error) {
      setPendingStipulations((prevStipulations) => prevStipulations.filter((id) => id !== stipulationId));
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const downloadStipulationDocument = async (
    stipulation: CreditApplicationStipulation,
    documentId: string,
    coApplicantId?: string
  ) => {
    const { stipulationId } = stipulation;
    const fieldId = createStipulationFieldId(stipulation);

    const docToDownload = values[fieldId]?.find((record) => {
      if (record.id === documentId) {
        return true;
      }
      return false;
    });
    if (coApplicantId) {
      const { url } = await getCreditApplicationCoCustomerStipulationsSingleDocument(
        quoteId,
        assertDefined(creditApplicationId),
        coApplicantId,
        stipulationId,
        documentId
      );
      const response = await largeFileDownload(url);

      await parseAndDownloadBinaryFileBlob(docToDownload.name, response.data);
    } else {
      const { url } = await getCreditApplicationCustomerStipulationsSingleDocument(
        quoteId,
        assertDefined(creditApplicationId),
        stipulationId,
        documentId
      );
      const response = await largeFileDownload(url);

      await parseAndDownloadBinaryFileBlob(docToDownload.name, response.data);
    }
  };

  const mapStipulationsToFields = useCallback(
    (stipulations, documents) => {
      stipulations.forEach((stipulation) => {
        const fieldId = createStipulationFieldId(stipulation);
        const documentRecords = documents.filter((record) => record.stipulationId === stipulation.stipulationId);

        if (documentRecords.length > 0) {
          setShowLoadingSpinner(true);
          setFieldValue(
            fieldId,
            documentRecords.map((documentRecord) => ({
              name: documentRecord.displayLabel,
              mimeType: SUPPORTED_FILE_FORMATS[documentRecord.documentFormat],
              id: documentRecord.documentId,
              tags: [fieldId, `${documentRecord.documentId}`],
            }))
          );
        } else {
          setFieldValue(fieldId, []);
        }
        setShowLoadingSpinner(false);
      });
    },
    [setFieldValue]
  );
  useEffect(() => {
    Analytics.push(documentUploadEvent);
  }, []);

  useEffect(() => {
    const loadStipulations = async () => {
      setShowLoadingSpinner(true);
      if (creditApplicationId) {
        try {
          const { documents: stipulations } = await getCreditApplicationCustomerStipulations(
            quoteId,
            assertDefined(creditApplicationId)
          );

          setCustomerStipulations(stipulations);

          const { documents } = await getCreditApplicationCustomerStipulationsDocuments(
            quoteId,
            assertDefined(creditApplicationId)
          );
          mapStipulationsToFields(stipulations, documents);

          if (applicantCreditScoreIdCoCustomer) {
            const { documents: coStipulations } = await getCreditApplicationCoCustomerStipulations(
              quoteId,
              assertDefined(creditApplicationId),
              applicantCreditScoreIdCoCustomer
            );

            setCoCustomerStipulations([...coStipulations]);
            const { documents: coDocuments } = await getCreditApplicationCoCustomerStipulationsDocuments(
              quoteId,
              assertDefined(creditApplicationId),
              applicantCreditScoreIdCoCustomer
            );
            mapStipulationsToFields(coStipulations, coDocuments);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
          setShowLoadingSpinner(false);
          store.dispatch(loadingActions.setLoading(false));
        } finally {
          setShowLoadingSpinner(false);
          store.dispatch(loadingActions.setLoading(false));
        }
      }
    };

    loadStipulations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStipulationsToFields, creditApplicationId, applicantCreditScoreIdCoCustomer]);

  // Check if can automatically skip and finish credit application
  useEffect(() => {
    const unskippableMandatoryCustomerStipulations = mandatoryCustomerStipulations.filter(
      ({ stipulationStatus }) => stipulationStatus === 'CREATED' || stipulationStatus === 'REFUSED'
    );
    const unskippableMandatoryCoCustomerStipulations = mandatoryCoCustomerStipulations.filter(
      ({ stipulationStatus }) => stipulationStatus === 'CREATED' || stipulationStatus === 'REFUSED'
    );

    const isUnskippableSipulationsLeft = Boolean(
      [...unskippableMandatoryCoCustomerStipulations, ...unskippableMandatoryCustomerStipulations].length
    );

    setSavedDocuments(!isUnskippableSipulationsLeft);
  }, [mandatoryCustomerStipulations, mandatoryCoCustomerStipulations, setSavedDocuments]);

  if (showLoadingSpinner) {
    return <SpinnerSection />;
  }

  if (savedDocuments) {
    return <ConfirmationPage setSavedDocuments={setSavedDocuments} />;
  }

  return (
    <>
      <ContentSection pageWrap className="u-pv-none">
        <Paragraph>{t('step5:descriptions:text')}</Paragraph>
        <Heading level={4} className="u-mt">
          {t('step5:headers:applicant')}
        </Heading>
        {mandatoryCustomerStipulations.length > 0 && (
          <Fieldset>
            <Paragraph className="u-mt u-font-size-fs5">{t('step5:mandatory') + '*'}</Paragraph>
            {mandatoryCustomerStipulations.map((stipulation) => {
              const fieldId = createStipulationFieldId(stipulation);
              return (
                <Fieldset.Row key={fieldId} className="u-text-center">
                  <Layout>
                    <Layout.Item>
                      <FileUpload
                        hasError={!!errors[fieldId]}
                        name={fieldId}
                        loading={pendingStipulations.includes(stipulation.stipulationId)}
                        descriptionText={stipulation.stipulationTypeTranslation || stipulation.description}
                        descriptionSupplementaryText={t('step5:upload:identification:description')}
                        buttonText={t('step5:buttons:upload')}
                        canDelete={stipulation.stipulationStatus !== 'ACTIVE'}
                        onFileRemove={(docId) => deleteStipulationDocument(stipulation, docId!)}
                        onFileDownload={(docId) => downloadStipulationDocument(stipulation, docId!)}
                        error={errors[fieldId] as string}
                        onChange={(event) => {
                          handleFieldData(
                            event,
                            `credit-application-stipulation-${stipulation.stipulationId}-${stipulation.stipulationType}`,
                            stipulation.stipulationId
                          );
                        }}
                      />
                    </Layout.Item>
                  </Layout>
                </Fieldset.Row>
              );
            })}
          </Fieldset>
        )}
        {optionalCustomerStipulations.length > 0 && (
          <Fieldset>
            <Paragraph className="u-mt u-font-size-fs5">{t('step5:optional')}</Paragraph>
            {optionalCustomerStipulations.map((stipulation) => {
              const fieldId = createStipulationFieldId(stipulation);
              return (
                <Fieldset.Row key={fieldId}>
                  <Layout>
                    <Layout.Item>
                      <FileUpload
                        hasError={!!errors[fieldId]}
                        name={fieldId}
                        loading={pendingStipulations.includes(stipulation.stipulationId)}
                        descriptionText={stipulation.stipulationTypeTranslation || stipulation.description}
                        descriptionSupplementaryText={t('step5:upload:identification:description')}
                        buttonText={t('step5:buttons:upload')}
                        canDelete={stipulation.stipulationStatus !== 'ACTIVE'}
                        onFileRemove={(docId) => deleteStipulationDocument(stipulation, docId!)}
                        onFileDownload={(docId) => downloadStipulationDocument(stipulation, docId!)}
                        onChange={(event) => {
                          handleFieldData(
                            event,
                            `credit-application-stipulation-${stipulation.stipulationId}-${stipulation.stipulationType}`,
                            stipulation.stipulationId
                          );
                        }}
                      />
                      {errors[fieldId] ? <div className="c-error-message">{errors[fieldId] as string}</div> : null}
                    </Layout.Item>
                  </Layout>
                </Fieldset.Row>
              );
            }) || null}
          </Fieldset>
        )}
      </ContentSection>
      {coCustomerStipulations.length ? (
        <ContentSection pageWrap>
          <Heading level={4} className="u-mt">
            {t('step5:headers:coApplicant')}
          </Heading>
          {mandatoryCoCustomerStipulations.length > 0 && (
            <Fieldset>
              <Paragraph className="u-mt u-font-size-fs5">{t('step5:mandatory') + '*'}</Paragraph>
              {mandatoryCoCustomerStipulations.map((stipulation) => {
                const fieldId = createStipulationFieldId(stipulation);
                return (
                  <Fieldset.Row key={fieldId}>
                    <Layout>
                      <Layout.Item>
                        <FileUpload
                          hasError={!!errors[fieldId]}
                          name={fieldId}
                          loading={pendingStipulations.includes(stipulation.stipulationId)}
                          descriptionText={stipulation.stipulationTypeTranslation || stipulation.description}
                          descriptionSupplementaryText={t('step5:upload:identification:description')}
                          buttonText={t('step5:buttons:upload')}
                          canDelete={stipulation.stipulationStatus !== 'ACTIVE'}
                          onFileRemove={(docId) =>
                            deleteStipulationDocument(stipulation, docId!, applicantCreditScoreIdCoCustomer)
                          }
                          onFileDownload={(docId) =>
                            downloadStipulationDocument(stipulation, docId!, applicantCreditScoreIdCoCustomer)
                          }
                          onChange={(event) => {
                            handleFieldData(
                              event,
                              fieldId,
                              stipulation.stipulationId,
                              applicantCreditScoreIdCoCustomer
                            );
                          }}
                        />
                        {errors[fieldId] ? <div className="c-error-message">{errors[fieldId] as string}</div> : null}
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                );
              })}
            </Fieldset>
          )}
          {optionalCoCustomerStipulations.length > 0 && (
            <Fieldset>
              <Paragraph className="u-mt u-font-size-fs5">{t('step5:optional')}</Paragraph>
              {optionalCoCustomerStipulations.map((stipulation) => {
                const fieldId = createStipulationFieldId(stipulation);
                return (
                  <Fieldset.Row key={fieldId}>
                    <Layout>
                      <Layout.Item>
                        <FileUpload
                          hasError={!!errors[fieldId]}
                          name={fieldId}
                          loading={pendingStipulations.includes(stipulation.stipulationId)}
                          descriptionText={stipulation.stipulationTypeTranslation || stipulation.description}
                          descriptionSupplementaryText={t('step5:upload:identification:description')}
                          buttonText={t('step5:buttons:upload')}
                          canDelete={stipulation.stipulationStatus !== 'ACTIVE'}
                          onFileRemove={(docId) =>
                            deleteStipulationDocument(stipulation, docId!, applicantCreditScoreIdCoCustomer)
                          }
                          onFileDownload={(docId) =>
                            downloadStipulationDocument(stipulation, docId!, applicantCreditScoreIdCoCustomer)
                          }
                          onChange={(event) => {
                            handleFieldData(
                              event,
                              fieldId,
                              stipulation.stipulationId,
                              applicantCreditScoreIdCoCustomer
                            );
                          }}
                        />
                        {errors[fieldId] ? <div className="c-error-message">{errors[fieldId] as string}</div> : null}
                      </Layout.Item>
                    </Layout>
                  </Fieldset.Row>
                );
              })}
            </Fieldset>
          )}
        </ContentSection>
      ) : null}
      <ContentSection pageWrap>
        {children}
        <FormNavigationButtons
          disableForward={!(errors && Object.keys(errors).length === 0) || pendingStipulations.length > 0}
          canGoForward
          canGoBack={false}
          backName={t('step5:navigation:return')}
          forwardName={t('step5:navigation:next')}
        />
      </ContentSection>
    </>
  );
};

export function Step({ children }: React.PropsWithChildren) {
  const [savedDocuments, setSavedDocuments] = useState(false);
  // For now timeOutError will be displayed for any error as we don't have specific code to manage it
  const [timeOutError, setTimeOutError] = useState(false);
  return (
    <Formik
      initialValues={{}}
      validateOnBlur={false}
      validationSchema={validationSchemaForStep5}
      onSubmit={async (values, { resetForm }) => {
        Analytics.push(legitimationEvent);
        store.dispatch(loadingActions.setLoading(true));
        try {
          // This is a place to push forward after document upload
          // We don't konw the order of document upload for customer and co-customer. How this works?
          setSavedDocuments(true);
        } catch (error) {
          store.dispatch(loadingActions.setLoading(false));
          setTimeOutError(true);
        } finally {
          store.dispatch(loadingActions.setLoading(false));
          resetForm({});
        }
      }}
    >
      {(formProps) => {
        return (
          <Form onReset={formProps.handleReset} onSubmit={formProps.handleSubmit}>
            {/* <ContentSection pageWrap>
              <ShoppingCart formData={formData} dataTheme="default" />
            </ContentSection> */}
            <FormStep
              {...formProps}
              savedDocuments={savedDocuments}
              setSavedDocuments={setSavedDocuments}
              timeOutError={timeOutError}
            >
              {children}
            </FormStep>
          </Form>
        );
      }}
    </Formik>
  );
}
